import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Page } from '@monorepo/interfaces';
import { SnowflakeSearchParams, SnowflakeSearchResult } from '../../interfaces/workflows/SnowflakeSearchResult';
import { SnowflakeSearchService } from '../../services/snowflake-search.service';
import { ClearSnowflakeSearch, SearchSnowflake } from './snowflake-search.actions';

export interface SnowflakeSearchStateModel {
    results: SnowflakeSearchResult[];
    totalItems: number;
    isLoading: boolean;
    hasSearched: boolean;
    lastSearchParams: SnowflakeSearchParams | null;
}

@State<SnowflakeSearchStateModel>({
    name: 'snowflakeSearch',
    defaults: {
        results: [],
        totalItems: 0,
        isLoading: false,
        hasSearched: false,
        lastSearchParams: null
    }
})
@Injectable()
export class SnowflakeSearchState {
    private snowflakeSearchService = inject(SnowflakeSearchService);

    @Selector()
    static results(state: SnowflakeSearchStateModel): SnowflakeSearchResult[] {
        return state.results;
    }

    @Selector()
    static totalItems(state: SnowflakeSearchStateModel): number {
        return state.totalItems;
    }

    @Selector()
    static isLoading(state: SnowflakeSearchStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static hasSearched(state: SnowflakeSearchStateModel): boolean {
        return state.hasSearched;
    }

    @Selector()
    static lastSearchParams(state: SnowflakeSearchStateModel): SnowflakeSearchStateModel['lastSearchParams'] {
        return state.lastSearchParams;
    }

    @Action(SearchSnowflake)
    searchSnowflake(
        ctx: StateContext<SnowflakeSearchStateModel>,
        action: SearchSnowflake
    ): Observable<Page<SnowflakeSearchResult> | HttpErrorResponse> {
        this.setIsLoading(ctx, true);
        return this.snowflakeSearchService.search(action.params, action.page, action.pageSize).pipe(
            tap((response: Page<SnowflakeSearchResult>) => {
                ctx.patchState({
                    results: response.items,
                    totalItems: response.metadata.itemCount,
                    hasSearched: true,
                    lastSearchParams: action.params
                });
            }),
            finalize(() => this.setIsLoading(ctx, false))
        );
    }

    @Action(ClearSnowflakeSearch)
    clearSnowflakeSearch(ctx: StateContext<SnowflakeSearchStateModel>): void {
        ctx.setState({
            results: [],
            totalItems: 0,
            isLoading: false,
            hasSearched: false,
            lastSearchParams: null
        });
    }

    private setIsLoading(ctx: StateContext<SnowflakeSearchStateModel>, isLoading: boolean): void {
        ctx.patchState({ isLoading });
    }
}
